import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { FiShield, FiX } from 'react-icons/fi'

const STORAGE_KEY = 'cookie_consent_v1'

// Lee el estado actual de consentimiento desde localStorage
export const getConsent = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

// Aplica el consentimiento a Google Consent Mode v2
const applyConsent = ({ analytics, marketing }) => {
  if (typeof window === 'undefined' || !window.gtag) return
  window.gtag('consent', 'update', {
    analytics_storage: analytics ? 'granted' : 'denied',
    ad_storage: marketing ? 'granted' : 'denied',
    ad_user_data: marketing ? 'granted' : 'denied',
    ad_personalization: marketing ? 'granted' : 'denied',
  })
}

const persist = (consent) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...consent, ts: Date.now(), v: 1 }))
  } catch {}
}

export default function CookieBanner() {
  const [visible, setVisible] = useState(false)
  const [showSettings, setShowSettings] = useState(false)
  const [analytics, setAnalytics] = useState(true)
  const [marketing, setMarketing] = useState(false)

  useEffect(() => {
    const stored = getConsent()
    if (!stored) {
      setVisible(true)
    } else {
      // Reaplicar al cargar la página por si gtag no lo recogió en index.html
      applyConsent(stored)
    }
  }, [])

  const acceptAll = () => {
    const consent = { analytics: true, marketing: true }
    applyConsent(consent)
    persist(consent)
    setVisible(false)
  }

  const rejectAll = () => {
    const consent = { analytics: false, marketing: false }
    applyConsent(consent)
    persist(consent)
    setVisible(false)
  }

  const saveCustom = () => {
    const consent = { analytics, marketing }
    applyConsent(consent)
    persist(consent)
    setVisible(false)
    setShowSettings(false)
  }

  if (!visible) return null

  return (
    <div className="fixed inset-x-0 bottom-0 z-[9999] p-4 sm:p-6">
      <div className="max-w-3xl mx-auto bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
        {!showSettings ? (
          <div className="p-5 sm:p-6">
            <div className="flex items-start gap-3 mb-3">
              <div className="shrink-0 w-9 h-9 rounded-lg bg-blue-100 grid place-items-center">
                <FiShield className="w-4 h-4 text-blue-600" />
              </div>
              <div className="flex-1">
                <h2 className="text-base font-semibold text-gray-900">Privacidad y cookies</h2>
                <p className="mt-1 text-sm text-gray-600 leading-relaxed">
                  Usamos cookies necesarias para que la aplicación funcione, y cookies analíticas
                  (Google Analytics 4 con IP anonimizada) sólo si las aceptas. No usamos cookies de
                  publicidad por defecto. Puedes cambiar tu elección en cualquier momento.
                  {' '}
                  <Link to="/cookies" className="text-blue-600 hover:text-blue-800 underline">
                    Más información
                  </Link>.
                </p>
              </div>
            </div>
            <div className="mt-4 flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
              <button
                type="button"
                onClick={() => setShowSettings(true)}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Personalizar
              </button>
              <button
                type="button"
                onClick={rejectAll}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Rechazar todo
              </button>
              <button
                type="button"
                onClick={acceptAll}
                className="px-4 py-2 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg"
              >
                Aceptar todo
              </button>
            </div>
          </div>
        ) : (
          <div className="p-5 sm:p-6">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-base font-semibold text-gray-900">Configurar cookies</h2>
              <button
                type="button"
                onClick={() => setShowSettings(false)}
                className="text-gray-400 hover:text-gray-600"
                aria-label="Cerrar"
              >
                <FiX className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-3">
              <label className="flex items-start gap-3 p-3 rounded-lg border border-gray-200 bg-gray-50 cursor-not-allowed">
                <input type="checkbox" checked disabled className="mt-1" />
                <div>
                  <div className="text-sm font-medium text-gray-900">Necesarias <span className="text-xs text-gray-500">(siempre activas)</span></div>
                  <div className="text-xs text-gray-600 mt-0.5">Sesión, autenticación, preferencias y seguridad. Sin ellas la app no funciona.</div>
                </div>
              </label>
              <label className="flex items-start gap-3 p-3 rounded-lg border border-gray-200 cursor-pointer hover:bg-gray-50">
                <input
                  type="checkbox"
                  checked={analytics}
                  onChange={(e) => setAnalytics(e.target.checked)}
                  className="mt-1"
                />
                <div>
                  <div className="text-sm font-medium text-gray-900">Analíticas</div>
                  <div className="text-xs text-gray-600 mt-0.5">Google Analytics 4 con IP anonimizada para entender cómo se usa la web y mejorarla.</div>
                </div>
              </label>
              <label className="flex items-start gap-3 p-3 rounded-lg border border-gray-200 cursor-pointer hover:bg-gray-50">
                <input
                  type="checkbox"
                  checked={marketing}
                  onChange={(e) => setMarketing(e.target.checked)}
                  className="mt-1"
                />
                <div>
                  <div className="text-sm font-medium text-gray-900">Marketing</div>
                  <div className="text-xs text-gray-600 mt-0.5">Cookies para medir la efectividad de campañas publicitarias. Actualmente no las usamos, pero las preparamos para futuro.</div>
                </div>
              </label>
            </div>
            <div className="mt-4 flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
              <button
                type="button"
                onClick={() => setShowSettings(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={saveCustom}
                className="px-4 py-2 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg"
              >
                Guardar preferencias
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
